import { useState } from 'react';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback';

import { handleError } from './handleError';
import {
	useExperimentalQueryState,
	useExperimentalQueryStateActions,
} from './useExperimentalQueryState';
import type { QueryResult, QueryCallbackOptions, FetchPolicy } from './useExperimentalQuery';
import { usePollInterval } from './utils/usePollInterval';

type UseExperimentalQueryExecutionParams<TData, TVariables> = QueryCallbackOptions<TData> & {
	key: string;
	fetchData: (variables?: TVariables) => TData | Promise<TData>;
	fetchPolicy: FetchPolicy;
	isLazyQuery: boolean;
	pollInterval?: number;
};

export type QueryOutcome<TData> = {
	data?: TData;
	error?: any;
};

// Tracks in-flight requests to prevent duplicate concurrent queries.
// Stores promises that resolve to the query outcome (data or error).
export const inFlightPromises = new Map<string, Promise<QueryOutcome<any>>>();

export function useExperimentalQueryExecution<TData, TVariables = any>({
	key,
	fetchData,
	onCompleted,
	onError,
	transformError,
	fetchPolicy,
	isLazyQuery = false,
	pollInterval,
}: UseExperimentalQueryExecutionParams<TData, TVariables>): {
	queryState: QueryResult<TData> | undefined;
	execute: (variables?: TVariables, options?: { bypassCache?: boolean }) => Promise<void>;
	refetch: (variables?: TVariables) => Promise<QueryOutcome<TData>>;
	stopPolling: () => void;
} {
	// For no-cache: use local state per hook instance instead of shared state
	const [localState, setLocalState] = useState<QueryResult<TData> | undefined>();
	const sharedState = useExperimentalQueryState({ key }) as QueryResult<TData> | undefined;

	const { setQueryState } = useExperimentalQueryStateActions<TData>();

	// Choose which state to expose based on fetchPolicy
	const queryState = fetchPolicy === 'no-cache' ? localState : sharedState;

	const execute = useStableCallback(
		async (
			variables?: TVariables,
			options?: { bypassCache?: boolean; skipLoadingState?: boolean },
		) => {
			// If cache-first and we have data, return immediately (unless bypassCache is true, e.g., for polling)
			if (
				fetchPolicy === 'cache-first' &&
				queryState?.data !== undefined &&
				!options?.bypassCache
			) {
				onCompleted?.(queryState.data);
				return;
			}

			// Check if this exact query is already in flight (deduplication)
			const existingPromise = inFlightPromises.get(key) as Promise<QueryOutcome<TData>> | undefined;
			if (existingPromise) {
				// Wait for the shared promise
				const outcome = await existingPromise;

				// Set local state if no-cache policy. Other fetch policies use shared state
				if (fetchPolicy === 'no-cache') {
					if (outcome.error) {
						setLocalState((prev) => ({
							data: prev?.data,
							loading: false,
							error: outcome.error,
							called: true,
						}));
					} else if (outcome.data !== undefined) {
						setLocalState({ data: outcome.data, loading: false, error: undefined, called: true });
					}
				}

				// Call this component's callbacks
				if (outcome.error) {
					onError?.(outcome.error);
				} else if (outcome.data !== undefined) {
					onCompleted?.(outcome.data);
				}
				return;
			}

			// Create a Promise for the fetch operation that resolves to the outcome
			const fetchPromise = (async (): Promise<QueryOutcome<TData>> => {
				try {
					// Set loading state if not initial fetch
					const shouldSetLoadingState =
						!options?.skipLoadingState &&
						(isLazyQuery ||
							queryState?.loading === false ||
							queryState?.data !== undefined ||
							queryState?.error);

					if (shouldSetLoadingState) {
						// Update the appropriate state based on fetchPolicy
						if (fetchPolicy === 'no-cache') {
							setLocalState({
								data: queryState?.data,
								loading: true,
								error: undefined,
								called: true,
							});
						} else {
							setQueryState(key, {
								data: queryState?.data,
								loading: true,
								error: undefined,
								called: true,
							});
						}
					}

					const data = await fetchData(variables);

					if (fetchPolicy === 'no-cache') {
						setLocalState({ data, loading: false, error: undefined, called: true });
					} else {
						setQueryState(key, { data, loading: false, error: undefined, called: true });
					}

					onCompleted?.(data);
					return { data };
				} catch (error) {
					const finalError = await handleError({ error, transformError, onError });

					if (fetchPolicy === 'no-cache') {
						setLocalState((prev) => ({
							data: prev?.data,
							loading: false,
							error: finalError,
							called: true,
						}));
					} else {
						setQueryState(key, (prev) => ({
							data: prev.data,
							loading: false,
							error: finalError,
							called: true,
						}));
					}

					return { error: finalError };
				} finally {
					// Remove from in-flight map so future calls create a new request
					inFlightPromises.delete(key);
				}
			})();

			// Store the Promise in the Map before starting the fetch
			inFlightPromises.set(key, fetchPromise);

			await fetchPromise;
		},
	);

	// Note: This only updates the shared state, so any fetch policies (e.g. "no-cache") will be ignored.
	// Please implement local state usage if it's needed in the future.
	const refetch = useStableCallback(
		async (variables?: TVariables): Promise<QueryOutcome<TData>> => {
			try {
				setQueryState(key, {
					data: queryState?.data,
					loading: true,
					error: undefined,
					called: true,
				});

				const data = await fetchData(variables);

				setQueryState(key, { data, loading: false, error: undefined, called: true });
				onCompleted?.(data);

				return { data };
			} catch (error) {
				const finalError = await handleError({ error, transformError, onError });
				setQueryState(key, (prev: QueryResult<TData>) => ({
					data: prev.data,
					loading: false,
					error: finalError,
					called: true,
				}));
				throw finalError;
			}
		},
	);

	const { stopPolling } = usePollInterval(pollInterval, execute, fetchPolicy);

	return { queryState, execute, refetch, stopPolling };
}
